import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';

import { ImageData, ImageSource, IMediaItem, MediaImage } from '../../../_shared/models';
import type { MediaItemFetchPriority } from '../../../_shared/types/media/media-item-fetch-priority.type';
import type { MediaItemObjectFit } from '../../../_shared/types/media/media-item-object-fit.type';
import type { MediaItemObjectPosition } from '../../../_shared/types/media/media-item-object-position.type';

import { ImageSourceService } from './image-source.service';

type RoundedCorners = [boolean, boolean, boolean, boolean] | boolean;

@Component({
    selector: 'sirius-image-item',
    templateUrl: './image-item.component.html',
    styleUrl: './image-item.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [],
    host: {
        'class': 'sir-image-item',
        '[class.is-contain]': 'resolvedObjectFit() === "contain"',
        '[class.is-cover]': 'resolvedObjectFit() === "cover"',
        '[class.is-fill]': 'resolvedObjectFit() === "fill"',
        '[class.is-scale-down]': 'resolvedObjectFit() === "scale-down"',
        '[class.is-rounded]': 'isRounded()',
        '[class.is-round]': 'round()',
        '[class.is-fluid]': 'fluid()',
        '[attr.data-object-fit]': 'resolvedObjectFit()',
    },
})
export class ImageItemComponent {
    private readonly imageSourceService = inject(ImageSourceService);

    readonly image = input<MediaImage | IMediaItem | undefined>();
    readonly imageData = input<ImageData | undefined>();
    readonly objectFit = input<MediaItemObjectFit>('contain');

    /** @deprecated Use `objectFit` instead. */
    readonly fit = input<MediaItemObjectFit | undefined>();

    readonly objectPosition = input<MediaItemObjectPosition | undefined>();
    readonly autocrop = input(true);
    readonly itemprop = input<string | undefined>();
    readonly roundedCorners = input<RoundedCorners>(false);
    readonly round = input(false);
    readonly fluid = input(false);
    readonly alt = input<string | undefined>();
    readonly fetchPriority = input<MediaItemFetchPriority | undefined>();

    readonly loaded = output<void>();

    readonly resolvedObjectFit = computed<MediaItemObjectFit>(() => this.fit() ?? this.objectFit());

    readonly mediaImage = computed(() => {
        const image = this.image();
        return this.isMediaImage(image) ? image : undefined;
    });

    readonly resolvedAlt = computed(() => this.alt() ?? this.image()?.title ?? '');

    readonly src = computed(() => this.imageService()?.getSrc() ?? this.mediaImage()?.image.url ?? '');

    readonly srcset = computed(() => this.imageService()?.getSrcSet() ?? '');

    readonly imageSources = computed<ImageSource[]>(() => {
        if (!this.imageData()) return [];
        return this.imageService()?.getPictureSources() ?? [];
    });

    readonly isRounded = computed(() => {
        const roundedCorners = this.roundedCorners();
        return roundedCorners === true || (Array.isArray(roundedCorners) && roundedCorners.some(Boolean));
    });

    private readonly imageService = computed(() => {
        const image = this.mediaImage()?.image;
        if (!image?.source) return undefined;

        return this.imageSourceService.getImageService(image.source, {
            url: image.url,
            dimensions: this.imageData()?.dimensions ?? [],
            crop: this.autocrop(),
            focusPoint: image.focusPoint,
        });
    });

    private isMediaImage(image: IMediaItem | MediaImage | undefined): image is MediaImage {
        return !!image && 'image' in image;
    }

    onLoaded(): void {
        this.loaded.emit();
    }
}
